/**
 * THE REP WORKLIST — what do I act on right now?
 *
 * Brian, as a rep: *"I'm having trouble - when I act as a rep I don't know how to
 * quickly find my assignments as to every championship I need to reach out on in
 * a quick form and fashion."*
 *
 * ⚠️ THE ANSWER IS NOT A BETTER FILTER. A rep holds 1,100-1,941 championships
 * (measured 2026-10-03: Jennifer 1941 · Zach 1577 · Philip 1563 · Ilia 1346 ·
 * Dalton 1314 · Jonathan 1286 · Chris 1176 · Faith 1106 · Emily 3). Nobody scans
 * 1,600 rows, so filtering the whole book better still leaves the rep deciding.
 * This returns a SHORT RANKED SET — `TARGET_ROWS` of them — ordered by how much
 * they need acting on today.
 *
 * PURE. No React, no fetch, no I/O, and it imports only the pure date/pursuit
 * machinery from `lib/championships.js` rather than re-deriving any of it — the
 * two-copies-of-one-rule fault that broke Canva drill-in (v1.36.1) and that
 * `stageTally` was written to end. `verify-worklist.mjs` drives this file.
 */
import {
  daysToEvent, daysSinceHeld, ownerName, dateQualifier, DATE_CONFIDENCE_LABEL,
  isPriorSeason, recordSeasonYear, currentSeasonYear, seasonLabel,
  inDayWindow, dayKey, addDays, SEASON_START_MONTH,
} from '../../../lib/championships.js'
import { opportunityValue, byRevenue, revenueSortKey } from './opportunityValue.js'
import { rowForRecord, chainFromUnitsRow, missingReason } from './unitsTable.js'
import { periodTotal } from './periodTotal.js'
import { collapseForWorklist } from '../pursuit/splitFamily.js'

/* How many rows a worklist aims at. Brian: "maybe 10-30 rows". The cap is a
 * DISPLAY decision, so `rank()` reports what it held back rather than dropping
 * it — a count the UI can state, per the standing never-silently-truncate rule. */
export const TARGET_ROWS = 30

/** Brian's standing rule: a championship is worked a MINIMUM of 30 days out. */
export const WORK_WINDOW_DAYS = 30

/**
 * How far out a SOURCED date can still raise an alarm. Matches the library's
 * `LOOKAHEAD_DAYS` (the last of `WINDOW_STEPS`) so the worklist and the stages
 * board agree on what "the horizon" means.
 */
export const ALARM_HORIZON_DAYS = 90

/**
 * Pursuit values that mean NOBODY HAS WORKED THIS YET.
 *
 * ⚠️ Deliberately mirrors `championships.js`'s own private `UNWORKED_PURSUIT`
 * (`'' | 'Not Started' | 'To Pursue'`) because that constant is not exported.
 * It is asserted equal to `isUncovered()`'s behaviour in verify §2 rather than
 * trusted — if the lib's set changes and this does not, the test fails instead
 * of the two quietly disagreeing.
 */
const UNWORKED = new Set(['', 'Not Started', 'To Pursue'])

/** Pursuit values that mean outreach HAS happened. Everything else is unworked. */
const WORKED = new Set([
  'Outreach In Progress', 'Outreach Completed', 'Won', 'Champion Crowned',
])

const S = (v) => (typeof v === 'string' ? v.trim() : v == null ? '' : String(v).trim())

/** Has anybody actually reached out on this record? */
export function isWorked(c) {
  return WORKED.has(S(c?.pursuit))
}

/** Is this record untouched — assigned to somebody and nobody has started? */
export function isUntouched(c) {
  return UNWORKED.has(S(c?.pursuit))
}

/** Does the record name a champion? Mirrors `isNoChampion` inverted. */
export function hasChampion(c) {
  return Boolean(c?.champion || c?.winningSchool)
}

/* ---- THE TIERS ----------------------------------------------------------- */

/**
 * Ranked by ACTIONABILITY, never alphabetically.
 *
 * `rank` is the sort key; `key` is what the UI groups on. The order is Brian's,
 * and each tier's `why` is the sentence the UI shows above the group — a rep who
 * cannot tell why a row is at the top does not trust the order.
 */
export const TIERS = [
  {
    key: 'JUST_CROWNED',
    rank: 1,
    label: 'Just crowned',
    why: 'A champion is resolved and nobody has reached out. This is the revenue moment and it decays fast.',
    tone: 'urgent',
  },
  /**
   * ⭐ A SPLIT WITH ITS CHAMPIONS NOT YET PLACED. Brian, 2026-10-26: "when we
   * just mark it a split into divisions - that sure leaves alot left to be
   * desired." A split is never an end state: a container with no Team row or
   * Individual grouping, a parent still holding a winner no row carries, or a
   * division with no winner is WORK, and it sits right under the revenue
   * moment because the event is usually already decided. Only `isSplitFix`
   * items (built by splitFamily.collapseForWorklist) ever land here.
   */
  {
    key: 'SPLIT_TO_FINISH',
    rank: 2,
    label: 'Split, champions still to place',
    why: 'These championships crown several winners. Each team champion needs its own row and every individual winner goes under one Individual (indy ring) grouping. The action on each card says exactly what is missing.',
    tone: 'attention',
  },
  {
    key: 'CROWNING_SOON',
    rank: 3,
    label: `Crowning within ${WORK_WINDOW_DAYS} days`,
    why: `Worked a minimum of ${WORK_WINDOW_DAYS} days out. Check the date qualifier before you plan around it.`,
    tone: 'warn',
  },
  /**
   * ⚠️ THIS TIER WAS "ASSIGNED BUT UNTOUCHED" AND THE LABEL WAS THE DEFECT.
   *
   * Measured 2026-10-03 against all nine real books: that rule put 962-1,940
   * records per rep in it, because nearly every `sr_pursuit_status` on the
   * object is '' / Not Started / To Pursue. So it flagged ~95% of the book as
   * an exception. A board that calls almost everything a problem is a board a
   * rep learns to ignore — the same failure as a bare zero, inverted.
   *
   * The alarm is now EVIDENCE, not activity: a SOURCED date approaching with
   * nobody on it. ⚠️ A PROJECTED DATE CAN NEVER RAISE IT — `sr_projected_date`
   * (13,049) outnumbers `sr_event_date` (11,518), so alarming on projections
   * would alarm on guesses and a rep would plan a week around one.
   */
  {
    key: 'SOURCED_UNWORKED',
    rank: 4,
    label: 'Sourced date approaching, nobody on it',
    why: 'The date on these is sourced, not projected, and no outreach has started. This is the one unworked case that is genuinely an alarm.',
    tone: 'attention',
  },
  /**
   * ⚠️ NO ALARM LANGUAGE HERE, DELIBERATELY. Not "untouched", not "overdue",
   * not "needs attention". This is the standing state of the book and it is
   * simply the work, in order: sourced dates first, then soonest.
   */
  {
    key: 'YOUR_BOOK',
    rank: 5,
    /* ⚠️ THE LABEL IS NOT "Your book", AND THE GATE IS RIGHT ABOUT WHY.
     * `check-ownership-language.mjs` refuses it in a label position: a rep is
     * assigned opportunities for a period, not given a territory, and a screen
     * that calls the list a possession is the first step toward arguing about
     * whose it is. The KEY stays `YOUR_BOOK` — verify-worklist asserts the key
     * and the tone, never this string — so the rename is copy only. */
    label: 'Assigned to you',
    why: 'Ranked, sourced dates first. This is the rest of your work in the order worth doing it.',
    tone: 'calm',
  },
]

export const TIER_BY_KEY = Object.fromEntries(TIERS.map((t) => [t.key, t]))

/**
 * Which tier does ONE record belong to? Exactly one, always — the tiers
 * partition the book, so nothing can be double-counted and nothing can vanish.
 *
 * ⚠️ TIER 1 IS "CROWNED AND UNWORKED", NOT "CROWNED". A crowned record somebody
 * has already called is not the revenue moment any more; leaving it at the top
 * would push the genuinely new crowns below it and the rep would stop trusting
 * the ordering. Tier 1 therefore shrinks as the work gets done, which is the
 * property that makes it a worklist rather than a report.
 */
export function tierOf(c, now = new Date()) {
  /* A split family's fix-it is never a crown and never "the rest of the book":
   * it is the specific work of placing champions. See SPLIT_TO_FINISH. */
  if (c?.isSplitFix) return 'SPLIT_TO_FINISH'
  if (hasChampion(c) && !isWorked(c)) return 'JUST_CROWNED'
  const d = daysToEvent(c, now)
  if (d != null && d >= 0 && d <= WORK_WINDOW_DAYS) return 'CROWNING_SOON'
  /* The alarm: SOURCED date, still ahead of us, inside the horizon, nobody on
   * it. Every clause earns its place — drop `sourced` and it alarms on
   * projections, drop the horizon and a date two years out reads as urgent. */
  if (d != null && d > WORK_WINDOW_DAYS && d <= ALARM_HORIZON_DAYS
    && dateVoice(c).sourced && isUntouched(c)) return 'SOURCED_UNWORKED'
  return 'YOUR_BOOK'
}

/* ---- DATE HONESTY -------------------------------------------------------- */

/**
 * ⚠️ A PROJECTED DATE MUST NOT LOOK LIKE A SOURCED ONE, or a rep plans their
 * week around a guess. Measured 2026-10-03: `sr_event_date` 11,518 sourced vs
 * `sr_projected_date` 13,049 projected and only 146 `sr_confirmed_date`, so the
 * projected case is the MAJORITY, not the exception.
 *
 * Returns the qualifier the row renders. `sourced` is the boolean the UI styles
 * on; the label is the words. Both come from the lib so the Team Rooms clock and
 * this row cannot describe one record differently.
 */
export function dateVoice(c) {
  const k = c?.dateConfidence || 'none'
  return {
    confidence: k,
    label: DATE_CONFIDENCE_LABEL[k] || k,
    qualifier: dateQualifier(c),
    sourced: k === 'confirmed' || k === 'event',
    /* A disputed date is worse than no date: it looks workable and is not. */
    disputed: k === 'conflict',
  }
}

/* ---- THE CHAMPION VOICE -------------------------------------------------- */

/**
 * FOUR VOICES, NOT FIVE — and the fifth is absent for a measured reason.
 *
 * `champion_detected_raw` is where `repair-defects` writes a RETRACTION
 * NARRATIVE, so a withdrawn crown reads as a decision rather than a gap. It is
 * writable at the operational tier (confirmed in `caller.mayWrite`, 29 fields)
 * and carried into the normalizer as `championDetectedRaw` — but it is
 * populated on ZERO of 15,111 records (measured 2026-10-03), because no crown
 * has ever been withdrawn
 * through that path. So the voice is WIRED AND SILENT rather than missing: the
 * moment a retraction is written it speaks, with no change here.
 *
 * ⚠️ IT IS NOT MAPPED ONTO `sr_champion_status`. Nobody has read that enum's
 * values, and guessing which one means "withdrawn" is the confident-wrong-data
 * failure this whole surface exists to avoid.
 */
export function championVoice(c) {
  /* 🔴 THE "SILENT FIFTH VOICE" WAS SPEAKING ON EVERY HUNT CROWN.
   *
   * Measured 2026-10-26: `/api/champion-resolve` writes the crown's REASON
   * into `champion_detected_raw` on every crown (resolve.js fieldsFor, the
   * `prov` block). Of the 281 crowns sr-champion-hunt-force landed in the
   * prior 8 days, 215 carried that text beside a champion (all 72 of the
   * night of 2026-10-25) - and this check read ANY text there as a
   * retraction, so each of them renders as WITHDRAWN with the champion
   * blanked. Latent on the usual load: the board snapshot does not carry the
   * field. It fires whenever the board falls back to the paged walk (a cold
   * snapshot, e.g. the first load after a backend deploy), which does. A
   * retraction is the text with NO champion beside it
   * (championState.js already reads it that way); a record that names a
   * champion is crowned, whatever provenance sits underneath. */
  const name = S(c?.champion) || S(c?.winningSchool)
  const retraction = S(c?.championDetectedRaw)
  if (retraction && !name) {
    return { kind: 'withdrawn', champion: '', note: retraction, blank: true }
  }
  if (!name) {
    return {
      kind: 'none',
      champion: '',
      /* NEVER A BARE BLANK. An unresolved champion is a job, so it says which. */
      note: S(c?.championSearchTag) === 'requested'
        ? 'No champion yet. A hunt is already requested on this record.'
        : 'No champion recorded yet.',
      blank: true,
    }
  }
  const year = c?.seasonYear ? ` ${c.seasonYear}` : ''
  const summary = c?.summary
  if (summary?.source === 'name-only') {
    return {
      kind: 'name-only',
      champion: `${name}${year}`,
      /* The honest reason a crowned record still cannot go to artwork. */
      note: 'The source gave nothing beyond the name, so this cannot go to artwork yet.',
      blank: false,
    }
  }
  return {
    kind: summary?.source === 'written' ? 'written' : 'composed',
    champion: `${name}${year}`,
    note: S(summary?.text),
    blank: false,
  }
}

/* ---- WHY IT IS ON THE LIST ----------------------------------------------- */

/**
 * One sentence per row naming why it is on THIS list now, in the tier's terms.
 * Never empty except on a split fix-it, whose own reason is already on the card
 * (`splitAction.reason`) and would otherwise be said twice.
 */
export function rowReason(tier, c, now = new Date()) {
  const d = daysToEvent(c, now)
  switch (tier) {
    case 'JUST_CROWNED':
      return c?.isIndyGroup
        ? 'Individual champions are recorded and nobody has reached out yet - one indy ring covers them all.'
        : 'Champion recorded and nobody has reached out yet. This is the revenue moment.'
    case 'SPLIT_TO_FINISH':
      return ''
    case 'CROWNING_SOON':
      return d === 0 ? 'Crowns today.' : `Crowns in ${d} day${d === 1 ? '' : 's'} - prepare before the champion is known.`
    case 'SOURCED_UNWORKED':
      return `Sourced date in ${d} days and no outreach has started.`
    default:
      return hasChampion(c)
        ? 'Champion recorded and outreach is under way.'
        : 'Assigned and not yet due; ranked by value, then date.'
  }
}

/* ---- WHY IT IS THEIRS ---------------------------------------------------- */

/**
 * ⚠️ `sr_owner_assign_reason` IS WRITABLE AND UNWRITTEN — measured, not assumed.
 *
 * It sits in `caller.mayWrite` (so `assignmentPlan.js:41-43`'s "NOT IN ANY TIER
 * / the dormant scaffold is unreachable" is STALE), and it is non-blank on 0 of
 * 15,111 records (measured 2026-10-03) and absent from the 38-property raw read
 * entirely. So reading
 * the field yields a blank for every rep on every row.
 *
 * The reason is therefore COMPUTED from the governing-body rule at render time.
 * `reasonFor()` in `assignmentPlan.js` composes the full sentence; this returns
 * the short form a row has space for, and takes the composed sentence as an
 * argument rather than importing the plan, so a record whose body has no rule
 * still gets an honest answer instead of a crash.
 */
export function ownershipVoice(c, composedReason = '') {
  if (c?.isIndyGroup) return { assigned: false, sourced: true, why: 'Grouped for review only. Each selected member retains its own assignee and authoritative support seats.' }
  const rep = ownerName(c)
  if (!rep) {
    return { rep: '', why: 'Nobody is assigned to this yet.', assigned: false, sourced: false }
  }
  const stored = S(c?.ownerAssignReason)
  if (stored) return { rep, why: stored, assigned: true, sourced: true }
  if (composedReason) return { rep, why: composedReason, assigned: true, sourced: false }
  const body = S(c?.governingBody)
  return {
    rep,
    why: body
      ? `Assigned by governing body: ${body}.`
      : 'Assigned, and the record names no governing body to explain why.',
    assigned: true,
    sourced: false,
  }
}

/* ---- ACTIONS ------------------------------------------------------------- */

/**
 * ⚠️ NO INERT ROWS — AND IT IS STRUCTURAL, NOT A CONVENTION.
 *
 * A list row with no onClick in its subtree has shipped on this surface before.
 * `open` is pushed FIRST and unconditionally, so `actionsFor()` can never return
 * an empty array and a row can never render without something to press. Verify
 * §6 asserts that over the whole universe rather than over a sample.
 *
 * `contact` is passed in rather than fetched: this file is pure, and the contact
 * resolves per-record through `useDossier` / `/api/hubspot/coach`. A row with no
 * contact yet still gets `open`, `hunt` and `exec`.
 */
/**
 * 🔴 THE WARNING THAT HAS TO BE ON THE CONTROL, NOT ONLY IN THE ANSWER.
 *
 * Brian: *"a projected date must not silently authorise or block a trade… make
 * it explicit either way, never implicit."*
 *
 * A championship whose date is PROJECTED rather than sourced cannot be shown to
 * be in the future, and it cannot be shown to be in the past either. The server
 * allows the trade on one — `eligibility.outcome` reads `may_trade_unsourced` —
 * and records that it rested on an unsourced date. Showing that only in the
 * response would mean the rep learns it AFTER handing a record over, which is
 * the wrong order for the one fact that might have changed their mind.
 *
 * Exported as a named constant because check-shipped-features.mjs greps the
 * BUILT bundle and the JSX transform splits multi-line element text.
 */
export const UNSOURCED_DATE_WARNING =
  'This championship’s date is not sourced, so nothing proves it has not already happened '
  + '— it only fails to prove that it has. The trade is allowed, and it will be recorded as '
  + 'resting on an unsourced date.'

/* The one sentence this file writes itself, and only because the server has not
 * been asked yet. Every other reason on these two controls is the server's. */
export const TRADE_NOT_ASKED_YET =
  'This screen has not had an answer from the trade service yet, so it does not know whether '
  + 'this record may move. That is not the same as a refusal — it will enable itself when the '
  + 'answer arrives, and if it never does, the service is down rather than you being blocked.'

/**
 * THE TRADE CONTROL, in one place, so the four live states and the four blocked
 * ones cannot drift apart.
 *
 * ⚠️ IT IS GATED ON WHAT THE SERVER ALLOWS, NOT ON WHAT THE CONSEQUENCE
 * SUGGESTS — the ClearCrown rule this file already follows for
 * `load-contacts`. Every refusal here carries the SERVER's sentence verbatim;
 * writing our own for something the server already explained is how two
 * surfaces start disagreeing about why a record is blocked.
 */
export function tradeAction(c, tradeState = null) {
  const t = tradeState || null
  const base = { kind: 'trade', primary: false }

  if (!t) {
    return { ...base, label: 'Trade this to another rep', disabled: true, why: TRADE_NOT_ASKED_YET }
  }

  /* ⚠️ OFF THE ROSTER IS NOT AN EMPTY INBOX. The service answers
   * `onRoster:false` with its own sentence precisely so the two can be told
   * apart, and flattening them would tell somebody they are clear to trade
   * when the truth is that we cannot place them. */
  if (t.onRoster === false) {
    return {
      ...base,
      label: 'Trade this to another rep',
      disabled: true,
      why: S(t.why) || 'The trade service does not place you on the roster, so it will not let you '
        + 'offer or accept a record. Ask for your roster entry before trying again.',
    }
  }

  const pending = t.pending || null
  if (pending && pending.direction === 'in') {
    /* ⭐ THE RIGHT NEXT STEP, NOT A BLOCKED BUTTON. A record offered TO this rep
     * needs a decision, and the decision is the live action. */
    return {
      ...base,
      label: `Accept or decline the trade from ${S(pending.withRep) || 'a teammate'}`,
      pending,
      primary: true,
      note: S(pending.reason),
    }
  }
  if (pending) {
    return {
      ...base,
      label: `Trade offered to ${S(pending.withRep) || 'a teammate'} — waiting on them`,
      disabled: true,
      pending,
      why: `${S(pending.reason) || 'A trade on this record is already pending.'} You still hold `
        + 'this record and it stays in your tiers until they accept. Nothing has moved.',
    }
  }

  /* ⚠️ A LOCKED WINDOW IS A SEPARATE REFUSAL FROM AN INELIGIBLE RECORD, and
   * the two needed opposite work: one waits for a date, the other never
   * becomes possible. */
  if (t.windowLocked) {
    return {
      ...base,
      label: 'Trade this to another rep',
      disabled: true,
      why: S(t.windowWhy) || 'The trade window is locked right now, so nothing can be offered or '
        + 'accepted until it reopens.',
    }
  }

  if (t.mayTrade === false) {
    return {
      ...base,
      label: 'Trade this to another rep',
      disabled: true,
      why: S(t.reason) || 'The trade service will not move this record and did not return a '
        + 'reason, which is itself worth reporting rather than retrying.',
    }
  }

  /* LIVE. 🔴 And the unsourced-date warning rides ON the control, before the
   * rep sends — `warn` is rendered as a visible sentence beside the button by
   * RepWorklist.jsx, not hidden in a `title` a phone never shows. */
  return {
    ...base,
    label: 'Trade this to another rep',
    warn: dateVoice(c).sourced ? '' : UNSOURCED_DATE_WARNING,
  }
}

/** The claim control. Same gating, one fewer party. */
export function claimAction(tradeState = null) {
  const t = tradeState || null
  const base = { kind: 'claim', label: 'Claim this — nobody is assigned to it yet', primary: false }
  if (!t) return { ...base, disabled: true, why: TRADE_NOT_ASKED_YET }
  if (t.onRoster === false) {
    return {
      ...base,
      disabled: true,
      why: S(t.why) || 'The trade service does not place you on the roster, so it will not record '
        + 'you against a record.',
    }
  }
  if (t.windowLocked) {
    return { ...base, disabled: true, why: S(t.windowWhy) || 'The assignment window is locked right now.' }
  }
  return base
}

/**
 * ⚠️ `tradeState` IS PASSED IN, BECAUSE THIS FILE IS PURE AND HOLDS NO
 * ELIGIBILITY DATA — the same reason `contact` and `distributeTo` are passed
 * in rather than fetched. `rank()` threads it from the options bag.
 *
 * Shape, all optional, every sentence the SERVER's:
 *   { onRoster, why, windowLocked, windowWhy,
 *     mayTrade, reason,
 *     pending: { direction: 'in' | 'out', withRep, reason } | null }
 *
 * `null` means this screen has not been told yet, which is a THIRD answer and
 * not the same as "you may not" — so the control stays disabled and says that,
 * rather than offering a trade the server has not agreed to or refusing one it
 * would have allowed.
 */
export function actionsFor(c, {
  contact = null, distributeTo = [], canLoadContacts = false, tradeState = null,
} = {}) {
  /* ⭐ A SPLIT ITEM CARRIES ITS OWN ACTION AND REASON (splitFamily.js), and it
   * rides the EXISTING `open` door rather than a new kind: `open` is the one
   * action MyWorklist.onAction already routes to the record, where the Team
   * rows, the Individual grouping and the record-the-divisions panel live. A
   * new kind would need a new handler, and an unhandled kind is a red box on
   * the first screen a rep opens. */
  if (c?.isIndyGroup) return [{ kind: 'open', label: 'Choose a member record for this indy ring', primary: true, disabled: true, why: 'A summary is not a record. Choose one actual member below.' }]
  const split = c?.worklistAction
  const out = [split
    ? { kind: 'open', label: S(split.label), why: S(split.reason), primary: true }
    : { kind: 'open', label: 'Open the record', primary: false }]

  /* ⭐ WHO TO CALL. The head coach is rarely the buyer — the athletic director
   * usually is — so the AD is offered FIRST when the committee has one, and the
   * role is always named beside the person. A row that names a school but not a
   * person leaves the rep doing the work. */
  if (contact?.phone) {
    out.push({
      kind: 'call', label: `Call ${contact.name}`, role: contact.role,
      href: `tel:${contact.phone}`, primary: true,
    })
  }
  if (contact?.email) {
    out.push({
      kind: 'email', label: `Email ${contact.name}`, role: contact.role,
      href: `mailto:${contact.email}`, primary: !contact?.phone,
    })
  }
  if (!contact) {
    /* NEVER imply a call that cannot be made. Contact coverage is uneven — Ohio
     * holds ~15,437 contacts and many states hold none — so the absence is
     * stated rather than rendered as a dead button.
     *
     * ⚠️ AND LOADING A STATE IS NOT A REP'S ACTION. Measured 2026-10-03:
     * `/api/coaches/states` and `/api/coaches/:state` both answer
     * 403 `forbidden` / action `system.run` to anything below exec+ops. So a
     * live "load this state" button on a rep's row would 403 on every press,
     * which is worse than no control (the ClearCrown rule: gate to what the
     * SERVER allows, not to what the consequence suggests). A rep gets the
     * explanation and who to ask; an exec or ops gets the actual control. */
    out.push(canLoadContacts
      ? { kind: 'load-contacts', label: 'Load contacts for this state', primary: false }
      : {
        kind: 'contact-gap',
        label: 'No contact on file for this school',
        disabled: true,
        why: 'Contacts are loaded per state by ops, not from here. Ask for this state to be loaded.',
        primary: false,
      })
  }

  if (!hasChampion(c) && !c?.isSplitFix) {
    const requested = S(c?.championSearchTag) === 'requested'
    out.push({
      kind: 'hunt',
      label: requested ? 'Champion hunt already requested' : 'Request a champion hunt',
      disabled: requested,
      why: requested ? 'This record is already in the hunt queue.' : '',
      primary: false,
    })
  }

  /* ⭐ ART · QUOTE · EMAIL · WIN, ON THE ROW (2026-10-26). Brian: "work list
   * doesn't include who the champion is either" - and a rep has to be able to
   * ACT from the row it is named on. These are the drawer's action console,
   * reached through the Command Center's existing record door (`?record=&act=`)
   * so there is one dossier, one crown modal and one pricing link, never a
   * second copy. A split fix-it carries its own single action instead: it is
   * not a sale until its champions are placed. */
  if (!c?.isSplitFix) {
    const crowned = hasChampion(c)
    out.push(
      { kind: 'art', label: 'Art', primary: crowned },
      { kind: 'quote', label: 'Quote', primary: false },
      { kind: 'campaign', label: 'Email', primary: false },
      { kind: 'win', label: 'Review winner', primary: false },
    )
  }

  /* ⚠️ DISABLED WITH A REASON, NOT LIVE, AND THAT IS A CORRECTION.
   *
   * This shipped as an enabled button — `{ kind: 'exec', label: 'Request an exec
   * push' }` — with the comment "Live, unlimited, routed to Rob Kass or John
   * Haggerty." It is not live. There is no execPush in src/lib/api.js and no
   * endpoint behind it, and MyWorklist's onAction handled only `kind: 'open'`,
   * so pressing it did nothing at all: no request, no error, no feedback.
   *
   * That was survivable while /my-worklist was unreachable. It stopped being
   * survivable in 2.89.0, when the route was linked for all nine reps — a
   * control that looks live and silently drops the ask is worse than no control,
   * because the rep believes an exec was told and stops chasing it.
   *
   * So it tells the truth instead, using this file's own existing idiom for a
   * control the caller cannot complete (see load-contacts above): disabled, with
   * `why` as the tooltip. The label and the routing intent stay visible, because
   * the capability is real and wanted — it is the wiring that is missing. Flip
   * `disabled` off in the same commit that adds the endpoint and the handler,
   * and not before. */
  out.push({
    kind: 'exec',
    label: 'Request an exec push',
    disabled: true,
    why: 'Not wired up yet — nothing is sent, so ask Rob Kass or John Haggerty directly for now.',
    primary: false,
  })

  /* ---- TRADE · ASK FOR HELP · ADD A REP -------------------------------- *
   *
   * Brian: *"You haven't given the rep a proper way to trade an account
   * either."* Correct — the trade machinery exists on the automation side and a
   * REP has never had a way to do it. He named THREE distinct actions, so these
   * are three controls and not one menu: hand it over, stay lead and ask for a
   * hand, and put another rep on it.
   *
   * 🔴 ALL THREE ARE DISABLED, EACH NAMING ITS OWN REASON, BECAUSE I CHECKED THE
   * ENDPOINT BEFORE WIRING A BUTTON — which is what Brian asked for and what
   * three dead controls today earned. Full findings in
   * docs/TRADE-HELP-SPLIT-2026-10-04.md. In short:
   *
   *   TRADE   `api.assignRep` CAN move `sr_assigned_rep` with a mandatory
   *           reason, audited. But there is no pending state on the record —
   *           no sr_trade_state, no proposed-to, no decline reason — so a
   *           "trade" built on it would land immediately whether or not the
   *           recipient agreed. That is precisely the one-way throw Brian
   *           forbade: *"the buck doesn't get passed."* Enabling this would be
   *           worse than leaving it off.
   *   HELP    nothing exists, and it is NOT a variant of assignRep: that
   *           function's whole job is to change the assignment, which is the
   *           one thing a help request must not do.
   *   ADD REP the record has exactly TWO support slots (`sr_support_rep_1/2`).
   *           Brian needs up to five contributors and named a conference case
   *           needing six. The field shape cannot hold it, and there is no
   *           split-percentage field at all.
   *
   * ⚠️ FLIP `disabled` OFF IN THE SAME COMMIT THAT ADDS THE ENDPOINT, NOT
   * BEFORE — the rule that "Request an exec push" broke.
   *
   * ⭐ AND THAT COMMIT IS THIS ONE. 2.107.0.
   *
   * The precondition named above is MET: `/api/assignment-trade` ships a real
   * PENDING state — propose, accept, decline, with a trail — so a trade sits
   * until the receiver agrees and the proposer holds the record until they do.
   * `api.tradePropose` / `api.tradeDecide` are the doors, and the `trade` branch
   * in `MyWorklist.onAction` lands in the same commit as this flag, because
   * flipping one without the other is a dead button on the primary rep surface
   * and that has already happened once.
   *
   * ⚠️ HELP and ADD REP ARE STILL DISABLED, and their reasons are unchanged:
   * nothing exists for a help request, and the record still holds exactly two
   * support reps with no role on either. Enabling three controls because one
   * became possible is how the first dead button shipped. */
  out.push(tradeAction(c, tradeState))

  /* ⭐ CLAIMING IS THE OTHER HALF OF THE ASK, AND IT IS NOT A TRADE.
   *
   * Brian asked for claiming as well as trading. The difference is structural
   * rather than semantic: a trade needs somebody to accept it, and there is
   * nobody to accept a record that names no rep — `ownershipVoice()` already
   * returns *"Nobody is assigned to this yet."* for 2,399 of them (measured
   * 2026-10-13T21:09Z over all 15,814). So claim POSTs straight through, still
   * with a mandatory reason, and still re-decided server-side: 409
   * `already_owned` is the honest answer to two reps claiming at once.
   *
   * Offered only on a record with no rep on it, so the control cannot be used
   * to take somebody else's work — that is what a trade is for. */
  if (!ownerName(c)) out.push(claimAction(tradeState))

  out.push({
    kind: 'help',
    label: 'Ask for help — you stay lead',
    disabled: true,
    why: 'Not wired up yet. This would keep you as lead and ask a teammate for a hand, which is a '
      + 'different thing from handing it over, and the record has no field for the request yet.',
    primary: false,
  })
  /* ⚠️ THREE SEPARATE THINGS THE SHAPE CANNOT HOLD, AND THE THIRD ARRIVED AFTER
   * I FIRST WROTE THIS REASON. Brian, 2026-10-04: *"A rep of adding another rep
   * should be able to make them both leads as well, which turns it into an even
   * split."* So the rep picks a ROLE and the role derives the money —
   * CONTRIBUTOR is 15% (15-30% as Philip allocates, one lead), CO-LEAD is an
   * even split between leads. That is money-safe by construction: a rep sets a
   * role, never a percentage, and Philip owns any numeric override.
   *
   * Which makes the field gap worse, not better:
   *   · `sr_support_rep_1/2` are two NAME strings — no capacity, no fifth or
   *     sixth person for the conference case.
   *   · `sr_lead_rep` is SINGULAR — two co-leads are not representable at all.
   *   · and neither carries a ROLE, so even one contributor cannot be told
   *     apart from one co-lead, which is the difference between 15% and 50%.
   *
   * A control offering the two roles against this shape would record the same
   * thing for both and the split would be wrong in the pay model. Stays
   * disabled until the role-bearing shape lands. */
  out.push({
    kind: 'add-rep',
    label: 'Add another rep to this',
    disabled: true,
    why: 'Not wired up yet, and not a small gap. The record holds only two support reps, so a lead '
      + 'plus five — or the six a conference opportunity needs — cannot be recorded; the lead field '
      + 'is single, so two co-leads cannot be recorded either; and nothing on the record carries a '
      + 'ROLE, so a contributor could not be told apart from a co-lead, which is the difference '
      + 'between a 15% and an even split. Philip sets the money; ask him directly for now.',
    primary: false,
  })

  /* DECISION 1 (Brian, 2026-10-03): Jennifer oversees all cheer and *"should
   * assign them to Kait and Abby"*. Distribution is not built yet, so all 1,941
   * are genuinely hers and her worklist is ranked like everyone else's — the
   * ranking is what solves her volume problem. The action is offered only when
   * the caller supplies people to hand to, so NO NAME IS HARDCODED here and the
   * control simply does not exist for a rep with nobody under them. */
  if (distributeTo.length) {
    out.push({ kind: 'distribute', label: 'Distribute to a teammate', options: distributeTo, primary: false })
  }
  return out
}

/* ---- THE SORT ------------------------------------------------------------ */

/**
 * Within a tier, the most time-critical first.
 *
 * ⚠️ A SOURCED DATE OUTRANKS A PROJECTED ONE AT THE SAME DISTANCE. Two records
 * both "14 days out" are not equally real when one is a guess, and putting the
 * guess above the sourced event is how a rep's week gets planned around a
 * projection. Ties then fall to the full name so the order is STABLE between
 * loads — an unstable worklist re-orders under the rep's thumb mid-scroll.
 * (withinTier is below newestCrownFirst.)
 */
/**
 * 🔴 IN THE CROWN TIER, THE NEWEST CROWN LEADS. The tier exists because a
 * crown "decays fast", yet it sorted by event date ascending, so the OLDEST
 * unworked crowns led and last night's sank: measured 2026-10-26, 64 of the 72
 * crowns the hunt landed on the night of 2026-10-25 ranked outside their rep's
 * first 30 rows. Compared by ET-agnostic calendar day of the crown so one
 * night's crowns tie and fall through to the existing order. A record with no
 * crown time (older snapshot) sorts after those that have one, never above.
 */
export function newestCrownFirst(a, b) {
  const day = (c) => { const t = Date.parse(S(c?.championDetectedAt)); return Number.isFinite(t) ? Math.floor(t / 86400000) : null }
  const ad = day(a), bd = day(b)
  if (ad === bd) return 0
  if (ad == null) return 1
  if (bd == null) return -1
  return bd - ad
}

export function withinTier(a, b, now = new Date()) {
  const av = dateVoice(a)
  const bv = dateVoice(b)
  if (av.sourced !== bv.sourced) return av.sourced ? -1 : 1
  const ad = daysToEvent(a, now)
  const bd = daysToEvent(b, now)
  const aHas = ad != null
  const bHas = bd != null
  if (aHas !== bHas) return aHas ? -1 : 1
  if (aHas && bHas && ad !== bd) return ad - bd
  /* For the look-back tiers, most recently concluded first. */
  const as = daysSinceHeld(a, now)
  const bs = daysSinceHeld(b, now)
  if (as != null && bs != null && as !== bs) return as - bs
  return S(a?.name).localeCompare(S(b?.name))
}

/* ---- THE EMPTY STATES ---------------------------------------------------- */

/**
 * ⚠️ NEVER A BARE ZERO. "Nothing needs action today", "we could not load your
 * work" and "you are scoped to nothing" are three different sentences, and a
 * non-exec was once silently scoped to an orders-derived book where every count
 * collapsed to 0 while an exec saw the truth. The backend has since fixed the
 * root cause by scoping on `sr_assigned_rep` — 9/9 identities return
 * `scope.mode:"own"` summing exactly to 11,312 (measured 2026-10-03) — but the
 * UI must still tell the
 * three apart, because the next regression will look identical to a quiet day.
 */
export const EMPTY = {
  LOADING: 'loading',
  FAILED: 'failed',
  UNMAPPED: 'unmapped',
  NO_BOOK: 'no-book',
  ALL_CLEAR: 'all-clear',
}

export function emptyKind({ status = 'ready', error = null, bookSize = 0, rows = 0, scopeMode = 'own' } = {}) {
  if (status === 'loading') return EMPTY.LOADING
  if (status === 'error' || error) return EMPTY.FAILED
  if (scopeMode === 'unmapped') return EMPTY.UNMAPPED
  if (bookSize === 0) return EMPTY.NO_BOOK
  if (rows === 0) return EMPTY.ALL_CLEAR
  return null
}

export const EMPTY_COPY = {
  [EMPTY.LOADING]: {
    headline: 'Loading your work',
    body: 'Reading your assigned championships.',
    tone: 'calm',
  },
  [EMPTY.FAILED]: {
    headline: 'We could not load your work',
    /* ⚠️ This must never be mistaken for a quiet day. */
    body: 'This is a failure to read, NOT an empty worklist. Your assignments are unchanged. Retry, and if it keeps failing say so rather than assuming there is nothing to do.',
    tone: 'danger',
  },
  [EMPTY.UNMAPPED]: {
    headline: 'You are not mapped to a book',
    body: 'Your sign-in does not match an assigned rep on the championship object, so there is nothing to scope to. That is a roster question, not an empty day.',
    tone: 'danger',
  },
  // A zero returned book is not evidence that allocation is currently running.
  // Keep it distinct from an unreadable feed and from a mapped book with no
  // actions in the selected window; do not invent a company-wide count.
  [EMPTY.NO_BOOK]: {
    headline: 'No championships returned for this scope',
    body: 'No championships were returned for this selected work scope. This read does not establish whether allocation is running or how many records are unassigned across the company. Check your scope with a manager, or open the championship list to see the records you are permitted to read.',
    tone: 'warn',
    actions: [{ kind: 'browse', label: 'Browse permitted championships' }],
  },
  [EMPTY.ALL_CLEAR]: {
    headline: 'Nothing needs action today',
    body: 'Your book loaded and every record is either worked or further out than the work window. This is a clear day, not a failure.',
    tone: 'good',
  },
}

/* ---- THE RANK ------------------------------------------------------------ */

/**
 * THE ONE ENTRY POINT. Takes a rep's whole book, returns the short ranked set.
 *
 * Returns `{ rows, groups, counts, held, bookSize, empty, caveats }`:
 *   rows      the ranked set, at most `limit`
 *   groups    the same rows grouped by tier, in tier order, each with its copy
 *   counts    per-tier counts over the WHOLE book, not just the rendered rows,
 *             so the summary can never under-report the way the stages summary
 *             did before `stageTally` (v2.41.0)
 *   held      how many qualified rows are not rendered — stated, never silent
 *   caveats   what is INCOMPLETE about the data behind this ranking, so a rep
 *             reads the thin tier as thin data rather than as a quiet week
 */
export function rank(book = [], {
  now = new Date(), limit = TARGET_ROWS, contacts = {}, distributeTo = [],
  composedReasons = {}, status = 'ready', error = null, scopeMode = 'own',
  canLoadContacts = false, includePriorSeasons = false,
  dateFrom = null, dateTo = null,
  unitsTable = null, orderBy = 'revenue',
  /* ⚠️ THREADED, NOT FETCHED — the same shape `contacts` and `distributeTo`
   * already use, because this file performs no I/O. Keyed by record id, with
   * `'*'` carrying the answer that is true of every row: whether this rep is on
   * the roster and whether the window is open. A per-record key overrides it,
   * which is where a PENDING trade lands. An absent key leaves `tradeState`
   * null, and the control says it has not been told yet rather than guessing in
   * either direction. */
  tradeStates = {},
  /* The WHOLE loaded object, so a split family is recognised even when this
   * book is one rep's slice (a parent and its rows can sit in one book while
   * the name that links them is only resolvable against everything). */
  universe = null,
  /* Record ids a link asked to open (`?focus=`). Never changes the order. */
  focusIds = [],
} = {}) {
  const bookSize = book.length
  const counts = Object.fromEntries(TIERS.map((t) => [t.key, 0]))
  const binned = Object.fromEntries(TIERS.map((t) => [t.key, []]))

  /* 🔴 PRIOR SEASONS ARE NOT WORK, SO THEY DO NOT ENTER THE TIERS.
   *
   * Brian: *"it brings up stale opportunities from 2024. Those are not relevant
   * and are completely spoken for."* ⚠️ AND THEY WERE AT THE TOP, NOT THE
   * BOTTOM: `tierOf` reads crowned-and-unworked as tier 1 JUST_CROWNED, "the
   * revenue moment", which a 2024 record with a champion and no logged outreach
   * matches exactly. Sorting them down would not have fixed it — Brian's words
   * are that they are not work at all, so they are partitioned out BEFORE
   * tiering rather than ranked within it.
   *
   * ⚠️ EXCLUDED IS NOT INVISIBLE, AND THAT IS THE STANDING RULE HERE. Nothing
   * on this board may silently vanish, so they are counted, their seasons are
   * named, and `includePriorSeasons` brings them straight back. The UI states
   * the count whether or not they are shown — the same treatment the row budget
   * and the collapsed governing bodies already get.
   *
   * ⚠️ AND ONLY A DATED RECORD CAN BE EXCLUDED. `recordSeasonYear` returns null
   * when nothing on the record places it in a season, and null is kept. A rule
   * that read "no date" as "old" would have emptied Jennifer's entire cheer
   * worklist, which carries no dates at all — a worse defect than the one being
   * fixed, and the exact kind of over-correction worth naming. */
  const priorSeason = includePriorSeasons ? [] : book.filter((c) => isPriorSeason(c, now))
  const priorSeasonYears = [...new Set(priorSeason.map((c) => recordSeasonYear(c)))]
    .filter((y) => y != null).sort((a, b) => b - a)
  const seasonScoped = includePriorSeasons ? book : book.filter((c) => !isPriorSeason(c, now))

  /* ---- THE DATE DIMENSION ---------------------------------------------- *
   *
   * Brian: *"In worklist it should be able to show a specific date… A rep must
   * be able to work A SPECIFIC DAY — what do I do today, what's next week."*
   *
   * ⚠️ AN UNDATED RECORD IS A THIRD ANSWER, NOT A MISS. `inDayWindow` returns
   * true / false / null, and null means the record carries no date at all so it
   * cannot be placed on any day. Folding null in with false would let a day
   * filter silently swallow the majority of some reps' work — Jennifer's cheer
   * records carry no dates whatsoever — and the rep would read an empty day as
   * a clear day. So the undated are counted separately and reported, and the UI
   * states how many are unplaceable whenever a window is on.
   *
   * ⚠️ AND THE WINDOW IS OFF BY DEFAULT. With no `dateFrom`/`dateTo` nothing is
   * excluded and `dateWindow.on` is false, so the view a rep opens is unchanged
   * — the date is a lens they reach for, not a scope imposed on them. */
  const hasWindow = Boolean(dateFrom || dateTo)

  /* 🔴 TWO RULES THAT COLLIDE, AND THE COLLISION IS RESOLVED OUT LOUD.
   *
   * The season partition exists to keep stale 2024 records out of the tiers.
   * A deliberate long lookback — Brian: *"look back more than 14 days"*, and
   * 180 days in October reaches into the prior season — exists to find
   * crowned-and-never-worked titles that are MORE urgent for having sat.
   * Applied in the shipped order, the partition silently amputates the
   * lookback: a rep asks for 180 days, gets 96, and nothing says so.
   *
   * ⚠️ THE EXPLICIT ASK WINS. A window whose `from` predates the season start
   * is a rep naming the period they want, and naming a period is a stronger
   * signal than a default that exists to clean up records nobody asked about.
   * So the window runs over the WHOLE book and the view states which seasons
   * it crossed — `spansSeasons` below. Honouring it silently would be its own
   * defect: a rep who sees 2025-2026 records with no explanation reasonably
   * concludes the stale-record fix broke. */
  const reachesPriorSeason = hasWindow && dateFrom instanceof Date
    && dateFrom < seasonStartDate(now)
  const datePool = reachesPriorSeason ? book : seasonScoped

  const dateVerdict = new Map()
  if (hasWindow) {
    for (const c of datePool) dateVerdict.set(c, inDayWindow(c, dateFrom, dateTo))
  }
  const outOfWindow = hasWindow ? datePool.filter((c) => dateVerdict.get(c) === false) : []
  const undatedOutside = hasWindow ? datePool.filter((c) => dateVerdict.get(c) === null) : []
  const inScopeRecords = hasWindow
    ? datePool.filter((c) => dateVerdict.get(c) === true)
    : seasonScoped

  /* ⭐ THE BOOK AS WORK, NOT AS STORAGE. Brian, 2026-10-26: every team champion
   * gets its own row, every individual winner of one championship goes under
   * ONE Individual (indy ring) item, and the parent never also counts as a
   * crown. `collapseForWorklist` does that over the in-scope set and reports
   * what it folded, so nothing leaves the list without a count. */
  const splitCollapse = collapseForWorklist(inScopeRecords, universe)
  const inScope = splitCollapse.book

  /* The seasons the in-scope set actually spans, so a window reaching back can
   * say so by name rather than leaving a rep to notice stray years. */
  const spansSeasons = hasWindow
    ? [...new Set(inScope.map((c) => recordSeasonYear(c)).filter((y) => y != null))]
      .sort((a, b) => b - a).map((y) => seasonLabel(y))
    : []

  for (const c of inScope) {
    const k = tierOf(c, now)
    /* A key no tier claims would be a silent drop. The tiers partition by
     * construction (REACHABLE is the else branch), and verify §3 asserts the
     * bins sum to the book, so this cannot hide a record. */
    if (!binned[k]) continue
    counts[k] += 1
    binned[k].push(c)
  }

  /* ---- THE MONEY, PER RECORD ------------------------------------------- *
   *
   * ⭐ One cacheable `/api/tam/units` call gives every record a figure, which is
   * what makes ordering the BOOK by dollars possible. The per-record route is
   * one call each and the biggest assignments run to several thousand records
   * (Brian's figures, 2026-10-04), so until this endpoint existed I refused to
   * ship a stand-in that re-ordered the 30 rows urgency had already chosen —
   * that would have looked like the feature and not been it.
   *
   * The arithmetic is NOT repeated here: the units row is adapted into the
   * `factors` shape and handed to `opportunityValue`, which is asserted by 31
   * cases including the proof that `fieldSize` and `penetration` cannot leak
   * in. One calculation decides what a rep quotes. */
  const valueOf = new Map()
  for (const c of inScope) {
    if (c?.isIndyGroup) {
      valueOf.set(c, { usd: null, low: null, high: null, units: null, price: null,
        confidence: { key: 'unmodelled', rank: 1, label: 'member review required' }, basis: '',
        reason: 'A grouped review item has no single verified account or quote. Select an actual member first.' })
      continue
    }
    const row = unitsTable ? rowForRecord(unitsTable, c) : null
    const chain = chainFromUnitsRow(row, unitsTable?.priceUsd ?? null)
    const v = chain ? opportunityValue(chain) : null
    valueOf.set(c, v && v.usd != null
      ? v
      /* ⚠️ NULL CARRIES THE ENDPOINT'S OWN SENTENCE. 734 records sit on
       * unmeasured sport+level rows — a visible population, not an edge case —
       * and the server already wrote the words for the person reading them.
       * Paraphrasing would lose the one thing they are for. */
      : { usd: null, low: null, high: null, units: row, price: null,
        confidence: { key: 'unmodelled', rank: 1, label: 'not modelled yet' },
        basis: '', reason: missingReason(unitsTable, c, row) })
  }

  /* ---- THE BOOK TOTAL -------------------------------------------------- *
   *
   * Brian: *"I need a total sum of opportunity, because you have a range for
   * each, give me a range for total sum of opportunities he is to pursue."*
   *
   * 🔴 IT TOTALS `inScope`, NOT `rows`. `rows` is the ranked THIRTY this view
   * draws; `inScope` is the whole book after the season, date-window and
   * person filters. Totalling what is on screen would answer a question
   * nobody asked and would look identical to the right answer — which is
   * exactly why it is computed here, from the same `valueOf` map the rows
   * read, rather than anywhere near the render. */
  const total = periodTotal(inScope.map((c) => valueOf.get(c)))

  /* ---- THE ORDER ------------------------------------------------------- *
   *
   * 🔴 REVENUE FIRST, URGENCY SECOND. Brian: *"Revenue is not a sort option
   * bolted onto urgency tiers — it is how a rep decides what to do today… A
   * big football championship crowning in three weeks outranks a small title
   * crowning tomorrow, and the view should say so."* (His worked figures are in
   * verify-revenue-rank.mjs §3, where they are asserted rather than quoted.)
   *
   * So in revenue mode the list is ONE sequence ordered by dollars across the
   * whole in-scope set, with the urgency tier as the tie-break — not a re-sort
   * inside each tier, which would have left the small title above the big one
   * whenever it was one tier more urgent. The tier still rides on every row
   * so the rep reads WHY something is urgent; it just no longer decides the
   * order on its own.
   *
   * ⚠️ URGENCY MODE IS KEPT AND IS NOT DEAD CODE. With no units table — a failed
   * or unloaded fetch — every figure is null, dollars cannot order anything, and
   * falling back to the tier order is the honest answer rather than a flat list
   * in arbitrary sequence. */
  const tierRank = Object.fromEntries(TIERS.map((t) => [t.key, t.rank]))
  /* 🔴 TWO DIFFERENT FAILURES, AND I COLLAPSED THEM INTO ONE SENTENCE.
   *
   * `revenueReady` was `priceUsd != null` and the header rendered "the units
   * table did not load" whenever it was false — so a table that LOADED FINE and
   * simply carries no price per unit reported itself as a failed fetch. Brian
   * saw both on one screen: the header saying "did not load" above cards saying
   * "loaded, no price". The cards were right.
   *
   * That is not a wording nit. Those two states have different owners and
   * different fixes — a failed fetch is a network or an auth problem on this
   * side, a missing price is the backend's to serve — and telling the wrong one
   * sends somebody to debug the wrong system. So they are tracked separately
   * and the UI says which actually happened. */
  const tableLoaded = Boolean(unitsTable)
  const priceAvailable = unitsTable?.priceUsd != null
  const revenueReady = tableLoaded && priceAvailable
  const useRevenue = orderBy === 'revenue' && revenueReady

  const ordered = []
  if (useRevenue) {
    /* 🔴 A CROWN IS NEVER OUT-RANKED BY A BIGGER UNCROWNED RECORD.
     *
     * Brian, 2026-10-26: "every crown the last hunt lands must show up on the
     * assigned rep's worklist with its action, without waiting for a manual
     * push." Revenue ordering flattened every tier into one dollar sort, so a
     * fresh $4k crown sat below thirty $9k records nobody can sell yet and was
     * HELD out of the rows - on the list in name only. The crown tier is the
     * revenue moment and decays fast (its own `why`), so it is pinned first and
     * ordered by dollars within itself; everything after it is the revenue
     * sort exactly as before. */
    const crowned = binned.JUST_CROWNED.map((c) => ({ tier: 'JUST_CROWNED', record: c }))
      .sort((a, b) => newestCrownFirst(a.record, b.record)
        || byRevenue(valueOf.get(a.record), valueOf.get(b.record)) || withinTier(a.record, b.record, now))
    ordered.push(...crowned)
    const flat = []
    for (const t of TIERS) if (t.key !== 'JUST_CROWNED') for (const c of binned[t.key]) flat.push({ tier: t.key, record: c })
    flat.sort((a, b) => {
      const d = byRevenue(valueOf.get(a.record), valueOf.get(b.record))
      if (d !== 0) return d
      /* Equal dollars (or both unmodelled): the more urgent tier wins, then the
       * existing within-tier rule, so the ordering is still total and stable. */
      const t = (tierRank[a.tier] || 99) - (tierRank[b.tier] || 99)
      if (t !== 0) return t
      return withinTier(a.record, b.record, now)
    })
    ordered.push(...flat)
  } else {
    for (const t of TIERS) {
      binned[t.key].sort((a, b) => (t.key === 'JUST_CROWNED' && newestCrownFirst(a, b)) || withinTier(a, b, now))
      for (const c of binned[t.key]) ordered.push({ tier: t.key, record: c })
    }
  }

  const taken = ordered.slice(0, limit)
  /* ⭐ A LINK INTO THE WORKLIST ALWAYS LANDS ON ITS ROW. The last-hunt sheet
   * links each crown to `/my-worklist?person=&focus=<id>`; a focused record
   * that ranks below the limit is added at the end, marked `focused`, rather
   * than opening a list it is not on. It still counts toward `held` below so
   * the "N more in your book" sentence stays true. */
  const focus = new Set((focusIds || []).map(String))
  for (const { record } of ordered) if (record?.isIndyGroup && record.memberRecords?.some((member) => focus.has(String(member.id)))) focus.add(String(record.id))
  const focusedExtra = focus.size
    ? ordered.slice(limit).filter(({ record }) => focus.has(String(record?.id ?? record?.hs_object_id)))
    : []
  const rows = [...taken, ...focusedExtra].map(({ tier, record }) => {
    const id = record?.id ?? record?.hs_object_id
    return {
      id,
      tier,
      focused: focus.has(String(id)),
      beyondLimit: focusedExtra.some((x) => x.record === record),
      /* ⚠️ NOTHING SHORTENED. No ellipsis, no truncation, no abbreviation — not
       * the championship, not the champion, not the governing body. "I told you
       * not to shorten anything." The row budget is a CSS problem (wrap, do not
       * clip) and never a data problem. */
      name: S(record?.name),
      governingBody: S(record?.governingBody),
      sport: S(record?.sport),
      level: S(record?.level),
      state: S(record?.state),
      date: record?.effDate || null,
      days: daysToEvent(record, now),
      dateVoice: dateVoice(record),
      champion: championVoice(record),
      /* WHY THIS ROW IS ON THE LIST, on the row (2026-10-26). The tier's long
       * `why` sits above the group; a card read on its own needs its own. */
      reason: rowReason(tier, record, now),
      ownership: ownershipVoice(record, composedReasons[id] || ''),
      contact: contacts[id] || null,
      /* The money, per row, so the view never recomputes it and cannot disagree
       * with the order it was sorted into. */
      value: record?.isIndyGroup ? null : valueOf.get(record) || null,
      /* ⭐ THE SPLIT, ON THE ROW. `splitAction` is the action and the reason
       * (a fix-it or an indy ring); `indy` lists every athlete with their
       * school so the one indy-ring item names everybody it covers; `family`
       * names the championship a Team row belongs to. Null on every other row. */
      splitAction: record?.worklistAction || null,
      indy: record?.isIndyGroup ? (record.references || []).map((a) => ({
        athlete: S(a.athlete), school: S(a.school), event: S(a.event), recordId: S(a.recordId) || null,
      })) : null,
      family: record?.splitFamily ? {
        role: record.splitFamily.role,
        parentName: S(record.splitFamily.parentName),
        teams: record.splitFamily.teams || [],
      } : null,
      actions: actionsFor(record, {
        contact: contacts[id] || null,
        distributeTo,
        canLoadContacts,
        /* The row's own answer if the service gave one, otherwise the answer
         * that is true of every row. `null` when neither exists. */
        tradeState: tradeStates[id] || tradeStates['*'] || null,
      }),
      record,
    }
  })

  const empty = emptyKind({ status, error, bookSize, rows: rows.length, scopeMode })

  /**
   * ⚠️ IS THIS LIST ACTUALLY PRIORITISED? SAY SO WHEN IT IS NOT.
   *
   * Jennifer Loveday-Donovan's 1,941 records carry NO event dates and one
   * crown (measured 2026-10-03: all 1,414 cheer titles are uncrowned), so
   * every ranking
   * clause ties and the order falls through to the stable name sort. Presenting
   * an alphabetical list as if it were ranked by urgency is a quiet lie, and
   * the rep would reasonably trust the top row. So the surface declares it.
   */
  const dated = rows.filter((r) => r.date).length
  const prioritised = rows.length === 0 || dated > 0

  return {
    rows,
    prioritised,
    datedRows: dated,
    unprioritisedReason: prioritised ? '' :
      'None of these records carries an event date yet, so this list is NOT prioritised by urgency — it is in name order. '
      + 'For cheer that is expected: no cheer championship has a date or a champion recorded yet.',
    groups: TIERS
      .map((t) => ({ ...t, rows: rows.filter((r) => r.tier === t.key), total: counts[t.key] }))
      .filter((g) => g.rows.length > 0),
    counts,
    held: Math.max(0, ordered.length - taken.length),
    bookSize,
    empty,
    emptyCopy: empty ? EMPTY_COPY[empty] : null,
    caveats: caveatsFor(inScope, counts),

    /* ⭐ WHAT THE SPLIT COLLAPSE DID, STATED. `folded` individual records went
     * into `groupings` indy-ring items; `containers` parents stopped counting as
     * crowns, `fixes` of them because something is still unplaced. The tiers
     * reconcile to the in-scope records through exactly these four numbers. */
    splitFamilies: {
      ...splitCollapse.collapsed,
      inScopeRecords: inScopeRecords.length,
      items: inScope.length,
    },

    /* ⚠️ REPORTED WHETHER OR NOT THEY ARE SHOWN. The count, the seasons they
     * are from, and the season we are in — so the control that brings them back
     * can say exactly what it would be showing, and a rep can never wonder
     * whether the list is hiding something. */
    season: {
      current: currentSeasonYear(now),
      currentLabel: seasonLabel(currentSeasonYear(now)),
      showingPriorSeasons: Boolean(includePriorSeasons),
      priorSeasonCount: priorSeason.length,
      priorSeasonYears,
      priorSeasonLabels: priorSeasonYears.map((y) => seasonLabel(y)),
    },

    /* ⚠️ THE ORDERING DECLARES ITSELF. A list claiming to be revenue-ranked
     * while the units table failed to load would be the same quiet lie as an
     * alphabetical list presented as prioritised — which this file already
     * guards against with `prioritised`. So the mode actually used is reported,
     * with whether the money was available at all. */
    ordering: {
      mode: useRevenue ? 'revenue' : 'urgency',
      requested: orderBy,
      revenueAvailable: revenueReady,
      /* The two halves, separately, so the UI names the failure that actually
       * happened rather than the one that is easier to describe. */
      tableLoaded,
      priceAvailable,
      /* `whyNotRevenue` is the sentence, decided once here so the view cannot
       * invent a third version of it. null when revenue IS available. */
      whyNotRevenue: revenueReady ? null
        : !tableLoaded
          ? 'The units table has not loaded, so there are no dollar figures to order by. That is a '
            + 'fetch that has not landed — not a statement about what anything is worth.'
          : 'The units table loaded, but it carries no price per unit, so a dollar figure cannot be '
            + 'computed from it. Nothing is guessed here: the price is the backend\'s to serve.',
      unitsMeasuredAt: unitsTable?.measuredAt || null,
      priceUsd: unitsTable?.priceUsd ?? null,
      priceSource: unitsTable?.priceSource || null,
      /* The visible population with no figure — Brian's 734, measured here off
       * the records in hand rather than pinned. */
      unmodelledRows: [...valueOf.values()].filter((v) => !v || v.usd == null).length,
      modelledRows: [...valueOf.values()].filter((v) => v && v.usd != null).length,
    },

    /* ⚠️ THE DATE LENS REPORTS ALL THREE POPULATIONS, not just the one shown.
     * `undated` is the number that stops a filtered day reading as a clear day
     * — for some reps it is most of their work. */
    dateWindow: {
      on: hasWindow,
      from: dateFrom ? dayKey(dateFrom) : null,
      to: dateTo ? dayKey(dateTo) : null,
      inWindow: inScope.length,
      outOfWindow: outOfWindow.length,
      undated: undatedOutside.length,
      /* ⚠️ THE TWO RULES THAT COLLIDED, REPORTED SO THE VIEW CAN SAY IT. */
      reachesPriorSeason,
      spansSeasons,
    },

    /* ⭐ THE PERIOD SUM. Brian: *"have my team sort by a date period for sum."*
     * Totalled over `inScope` — the period — never over `rows`, which is the
     * thirty this view draws. */
    total,

    /* 🔴 CROWNED-AND-WORKED vs CROWNED-AND-UNTOUCHED, AND THE HONEST LIMIT.
     *
     * Brian: *"a crowned record nobody has touched in 60 days is exactly the
     * signal the nudge system is being built to surface"* — and he is right
     * that it is the money in a long lookback.
     *
     * ⚠️ BUT I CANNOT EVIDENCE "UNTOUCHED". The only thing readable here is
     * `sr_pursuit_status`, which is a STAGE a human set, not an activity
     * record: a rep who called yesterday and never moved the stage is
     * indistinguishable from one who has not opened the record since it was
     * crowned. The real signal is `effort_events`, and that read answered 403
     * `assignment_locked` for another lane.
     *
     * So this reports what the STATUS says and labels it as exactly that.
     * `evidenced: false` is the flag the view renders as "no activity record
     * is readable" — because implying neglect that cannot be evidenced would
     * send a rep to apologise for work they already did. */
    crowned: {
      inPeriod: inScope.filter((c) => hasChampion(c)).length,
      statusWorked: inScope.filter((c) => hasChampion(c) && isWorked(c)).length,
      statusUntouched: inScope.filter((c) => hasChampion(c) && isUntouched(c)).length,
      evidenced: false,
      why: 'This is the pursuit STATUS somebody set by hand, not an activity record. '
        + 'A rep who called and never moved the stage reads the same as one who never '
        + 'opened it, so treat "not started" as a prompt to check, never as proof nobody '
        + 'has touched it.',
    },
  }
}

/* ---- THE DAY PRESETS ---------------------------------------------------- *
 *
 * "What do I do today" and "what's next week" are the two questions Brian named,
 * so they are named presets rather than a date picker a rep has to operate.
 *
 * ⚠️ PURE, AND `now` IS INJECTED. A preset that read the clock itself could not
 * be asserted — the same reason every date function in this file takes `now`.
 * Returns `{ from, to }` as local Dates, or nulls for `all`.
 */
export const DAY_PRESETS = [
  { key: 'all', label: 'All dates' },
  { key: 'today', label: 'Today' },
  { key: 'tomorrow', label: 'Tomorrow' },
  { key: 'next7', label: 'Next 7 days' },
  { key: 'next30', label: `Next ${WORK_WINDOW_DAYS} days` },
  /* 🔴 REAL PERIODS, NOT JUST DAY-COUNTS. Brian: *"I want to be able to have my
   * team sort by a date period for sum."* A rolling "next 30 days" is not a
   * month — it straddles two of them — and a rep planning work plans in the
   * calendar units everybody else in the business uses. A quarter likewise is
   * how a season gets talked about. These are calendar-aligned on purpose. */
  { key: 'thisMonth', label: 'This month' },
  { key: 'nextMonth', label: 'Next month' },
  { key: 'thisQuarter', label: 'This quarter' },
  /* 🔴 THE LOOKBACK IS AS LONG AS HE WANTS IT. Brian: *"And look back more than
   * 14 days."* 14 days was OUR decay assumption and it was never his — a title
   * crowned 60 days ago and never worked is not a colder opportunity than one
   * crowned yesterday, it is a more urgent one, because it has been sitting.
   * So the backward windows are first-class, not one footnote. */
  { key: 'last14', label: 'Crowned in the last 14 days' },
  { key: 'last30', label: 'Crowned in the last 30 days' },
  { key: 'last60', label: 'Crowned in the last 60 days' },
  { key: 'last90', label: 'Crowned in the last 90 days' },
  { key: 'thisSeason', label: 'This season so far' },
  /* ⚠️ CUSTOM IS A REAL PRESET KEY, NOT A MODE FLAG. `presetWindow` returns
   * nulls for it and the caller supplies the dates, so every other function in
   * this file keeps taking a plain `{from, to}` and nothing has to learn that
   * one period is special. */
  { key: 'custom', label: 'Pick dates' },
]

/** The last day of the month `d` falls in. */
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0)

/** 1 July of the season `now` falls in — SR files a season Jul→Jun. */
export const seasonStartDate = (now = new Date()) =>
  new Date(currentSeasonYear(now), SEASON_START_MONTH, 1)

export function presetWindow(key, now = new Date()) {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const y = today.getFullYear()
  const m = today.getMonth()
  switch (key) {
    case 'today': return { from: today, to: today }
    case 'tomorrow': return { from: addDays(today, 1), to: addDays(today, 1) }
    case 'next7': return { from: today, to: addDays(today, 7) }
    case 'next30': return { from: today, to: addDays(today, WORK_WINDOW_DAYS) }
    case 'last14': return { from: addDays(today, -14), to: today }
    case 'last30': return { from: addDays(today, -30), to: today }
    case 'last60': return { from: addDays(today, -60), to: today }
    case 'last90': return { from: addDays(today, -90), to: today }
    /* ⚠️ "This season so far" ENDS TODAY, not at the season's end. It is a
     * lookback — what has already been crowned this season — and running it
     * forward to June would make it a forecast wearing a lookback's name. */
    case 'thisSeason': return { from: seasonStartDate(now), to: today }
    /* ⚠️ A CALENDAR MONTH RUNS FROM ITS FIRST DAY, NOT FROM TODAY. On the 20th,
     * "this month" that began on the 20th would be a rolling window wearing a
     * calendar name — and a rep totalling "this month" means the month. */
    case 'thisMonth': return { from: new Date(y, m, 1), to: endOfMonth(today) }
    case 'nextMonth': return { from: new Date(y, m + 1, 1), to: endOfMonth(new Date(y, m + 1, 1)) }
    case 'thisQuarter': {
      const q = Math.floor(m / 3) * 3
      return { from: new Date(y, q, 1), to: endOfMonth(new Date(y, q + 2, 1)) }
    }
    /* `custom` deliberately falls through to nulls: the dates come from the
     * operator, and inventing a default range here would silently total a
     * window nobody chose. */
    default: return { from: null, to: null }
  }
}

/**
 * ⚠️ WHAT IS IMPERFECT, SAID OUT LOUD ON THE SURFACE ITSELF.
 *
 * Each tier rests on data that is incomplete, and a rep must be able to SEE
 * that rather than be misled by it. These are computed from the book in hand,
 * not hardcoded, so they stay true as the data improves and disappear when it
 * is fixed. The object grew 8 records in under a day: RECONCILE, NEVER PIN.
 */
/* `_counts` is accepted but deliberately unread: every caveat is derived from
 * the BOOK itself, so a caller that passes stale counts cannot make the surface
 * state a number the records do not support. Kept in the signature because the
 * tier counts are the obvious thing a future caveat would want. */
export function caveatsFor(book = [], _counts = {}) {
  const out = []
  const n = book.length
  if (!n) return out

  const noChampion = book.filter((c) => !hasChampion(c)).length
  /* ⚠️ AN EMPTY TIER 1 MUST READ AS A FACT ABOUT THE DATA, NOT A BROKEN SCREEN.
   * Measured 2026-10-03: Chris Powell and Ilia Jarostchuk hold ZERO crowned
   * records across 1,176 and 1,346, so for them this tier is legitimately empty every time
   * they open it. Without the measured reason beside it, an always-empty top
   * section is indistinguishable from a feature that does not work. */
  if (noChampion === n) {
    out.push({
      tier: 'JUST_CROWNED',
      text: `None of your ${n.toLocaleString()} records has a champion recorded, so "just crowned" is empty — that is the state of the data, not a fault in this view. It will fill as champions get resolved.`,
    })
  } else if (noChampion) {
    out.push({
      tier: 'JUST_CROWNED',
      text: `${noChampion.toLocaleString()} of your ${n.toLocaleString()} records have no champion recorded, so this tier is thinner than reality. A thin tier here is missing data, not a quiet week.`,
    })
  }

  /* The no-dates case, stated per book rather than assumed to be cheer. */
  const undated = book.filter((c) => !c?.effDate).length
  if (undated === n) {
    out.push({
      tier: 'ALL',
      text: `None of your ${n.toLocaleString()} records carries an event date, so this list cannot be ordered by urgency and is in name order instead.`,
    })
  }

  /**
   * ⚠️ OUT OF SEASON IS NOT AN EMPTY WORKLIST, AND IT IS NOT MISSING DATA.
   *
   * Measured 2026-10-03: Jennifer Loveday-Donovan has 764 sourced and 1,177
   * projected dates across 1,941 records, and `soon` and the alarm tier are
   * BOTH zero — because every cheer event is either past or more than 90 days
   * out. Cheer crowns in the spring and it is October. An earlier draft of this
   * surface was going to tell her "cheer records carry no event dates yet",
   * which is simply false and would have sent somebody to fix working data.
   */
  const dated = book.filter((c) => c?.effDate).length
  const inHorizon = book.filter((c) => {
    const d = daysToEvent(c)
    return d != null && d >= 0 && d <= ALARM_HORIZON_DAYS
  }).length
  if (dated > 0 && inHorizon === 0) {
    out.push({
      tier: 'ALL',
      text: `Nothing in your book crowns in the next ${ALARM_HORIZON_DAYS} days. Your ${dated.toLocaleString()} records are dated — they are simply out of season right now, so the urgent tiers are empty because there is nothing urgent, not because anything is missing.`,
    })
  }

  const projected = book.filter((c) => !dateVoice(c).sourced && c?.effDate).length
  if (projected) {
    const pct = Math.round((projected / n) * 100)
    out.push({
      tier: 'CROWNING_SOON',
      /* ⚠️ AT AN EXTREME RATIO THE ALARM TIER IS STRUCTURALLY SMALL AND THAT
       * NEEDS SAYING. Ilia Jarostchuk carries 40 sourced against 1,306
       * projected, so his alarm tier can only ever draw from those 40. Without
       * this, a near-empty alarm reads as a broken screen. */
      text: pct >= 80
        ? `${projected.toLocaleString()} of your ${n.toLocaleString()} records (${pct}%) carry a PROJECTED date rather than a sourced one. The alarm tier only ever draws on sourced dates, so it will be small for you — that is the state of the data, not a fault in this view.`
        : `${projected.toLocaleString()} of your dated records carry a PROJECTED date, not a sourced one. Projected dates are marked — check the qualifier before you plan a week around one.`,
    })
  }

  const noContact = book.filter((c) => !S(c?.state)).length
  if (noContact) {
    out.push({
      tier: 'ALL',
      text: `${noContact.toLocaleString()} of your records carry no state, which is also where contact coverage is thinnest. Those rows will not offer a call.`,
    })
  }

  const disputed = book.filter((c) => dateVoice(c).disputed).length
  if (disputed) {
    out.push({
      tier: 'CROWNING_SOON',
      text: `${disputed.toLocaleString()} of your records have an event date that contradicts their season year. They are marked "date disputed" and are not workable until corrected.`,
    })
  }
  return out
}
